import { createSlice, createAsyncThunk } from "@reduxjs/toolkit"
import { get } from "../../lib/request"
import { ACCOUNT_MANAGER_ROLE } from "../../lib/utils/roleTabConfig"

/** One row from collaborators API → Redux `currentUserRole`; [] → account manager. */
function currentUserRoleFromCollaboratorsResponse(rows) {
  if (!Array.isArray(rows) || rows.length === 0) return ACCOUNT_MANAGER_ROLE
  const accessLevel = rows[0]?.accessLevel
  if (accessLevel != null && String(accessLevel).trim() !== "") return String(accessLevel).trim()
  return null
}

function normalizeCollaboratorList(payload) {
  if (Array.isArray(payload)) return payload
  if (payload && typeof payload === "object" && Array.isArray(payload.data)) return payload.data
  return []
}

// Fetch collaborators for a client
export const fetchCollaborators = createAsyncThunk(
  "collaborators/fetch",
  async ({ clientId, userId }, { rejectWithValue, getState }) => {
    try {
      const token = getState().auth?.token
      if (!token) {
        throw new Error("Authentication required. Please login first.")
      }

      const data = await get(`collaborators/clients/${clientId}/users/${userId}`)

      return { data, clientId }
    } catch (err) {
      return rejectWithValue(err?.message || "Failed to fetch collaborators")
    }
  },
  {
    condition: ({ clientId, userId, force } = {}, { getState }) => {
      if (force) return true
      if (clientId == null || userId == null) return false
      const slice = getState().collaborators ?? {}
      const sameClient = String(slice.roleClientId ?? "") === String(clientId)
      if (slice.status === "loading" && sameClient) return false
      if (slice.status === "succeeded" && sameClient) return false
      return true
    },
  },
)

/** GET collaborators/clients/{clientId} — all property collaborators (roles list). */
export const fetchClientCollaboratorsList = createAsyncThunk(
  "collaborators/fetchClientList",
  async ({ clientId }, { rejectWithValue, getState }) => {
    try {
      const token = getState().auth?.token
      if (!token) {
        throw new Error("Authentication required. Please login first.")
      }
      if (clientId == null || String(clientId).trim() === "") {
        throw new Error("Client ID is required")
      }

      const data = await get(`collaborators/clients/${clientId}`)
      return { data: normalizeCollaboratorList(data), clientId }
    } catch (err) {
      return rejectWithValue(err?.message || "Failed to fetch client collaborators")
    }
  },
  {
    condition: ({ clientId, force } = {}, { getState }) => {
      if (force) return true
      if (clientId == null || String(clientId).trim() === "") return false
      const slice = getState().collaborators ?? {}
      const sameClient =
        String(slice.clientCollaboratorsListClientId ?? "") === String(clientId)
      if (slice.clientCollaboratorsListStatus === "loading" && sameClient) return false
      if (slice.clientCollaboratorsListStatus === "succeeded" && sameClient) return false
      return true
    },
  },
)

const initialState = {
  collaborators: [],
  currentUserRole: null,
  roleClientId: null,
  status: "idle", // idle | loading | succeeded | failed
  error: null,
  clientCollaboratorsList: [],
  clientCollaboratorsListClientId: null,
  clientCollaboratorsListStatus: "idle",
  clientCollaboratorsListError: null,
}

const collaboratorsSlice = createSlice({
  name: "collaborators",
  initialState,
  reducers: {
    clearCollaborators: (state) => {
      state.collaborators = []
      state.currentUserRole = null
      state.roleClientId = null
      state.status = "idle"
      state.error = null
      state.clientCollaboratorsList = []
      state.clientCollaboratorsListClientId = null
      state.clientCollaboratorsListStatus = "idle"
      state.clientCollaboratorsListError = null
    },
    clearCollaboratorsError: (state) => {
      state.error = null
    },
    setCurrentUserRole: (state, action) => {
      state.currentUserRole = action.payload
    },
    clearCurrentUserRole: (state) => {
      state.currentUserRole = null
      state.roleClientId = null
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch collaborators
      .addCase(fetchCollaborators.pending, (state, action) => {
        state.status = "loading"
        state.error = null
        const nextClientId = action.meta.arg?.clientId
        if (
          state.roleClientId == null ||
          String(state.roleClientId) !== String(nextClientId ?? "")
        ) {
          state.currentUserRole = null
        }
        if (nextClientId != null) state.roleClientId = nextClientId
      })
      .addCase(fetchCollaborators.fulfilled, (state, action) => {
        state.status = "succeeded"
        const rows = Array.isArray(action.payload.data) ? action.payload.data : []
        state.collaborators = rows
        state.roleClientId = action.payload.clientId
        state.currentUserRole = currentUserRoleFromCollaboratorsResponse(rows)
      })
      .addCase(fetchCollaborators.rejected, (state, action) => {
        state.status = "failed"
        state.error = action.payload || "Failed to fetch collaborators"
      })
      .addCase(fetchClientCollaboratorsList.pending, (state) => {
        state.clientCollaboratorsListStatus = "loading"
        state.clientCollaboratorsListError = null
      })
      .addCase(fetchClientCollaboratorsList.fulfilled, (state, action) => {
        state.clientCollaboratorsListStatus = "succeeded"
        state.clientCollaboratorsList = Array.isArray(action.payload.data)
          ? action.payload.data
          : []
        state.clientCollaboratorsListClientId = action.payload.clientId
        state.clientCollaboratorsListError = null
      })
      .addCase(fetchClientCollaboratorsList.rejected, (state, action) => {
        state.clientCollaboratorsListStatus = "failed"
        state.clientCollaboratorsListError =
          action.payload || "Failed to fetch client collaborators"
        state.clientCollaboratorsList = []
      })
  },
})

export const { clearCollaborators, clearCollaboratorsError, setCurrentUserRole, clearCurrentUserRole } =
  collaboratorsSlice.actions

export default collaboratorsSlice.reducer
